import { dateUtils } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'

import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { getParameterDefinitionInOut } from '../../uiModels/parameterOperations/helpers'
import type { IndexedParameterDefinitionCreateOperationWithMetaData } from '../../uiModels/parameterOperations/types'
import { NO_DATE_SYMBOL } from '../../util/constants'
import { useOperationsContext } from '../../workspace/operationsContext'
import { ARROW_LINE_WIDTH } from '../versionedTimeline/constants'
import { OperationVersionCircle } from '../versionedTimeline/OperationVersionCircle'
import type { useTimelineScale } from '../versionedTimeline/useTimelineScale'
import { ParameterDefinitionInOutTooltipContent } from './ParameterDefinitionInOutTooltipContent'
import { useIsParameterVersionActive } from './useIsParameterVersionActive'

type Props = {
  version: IndexedParameterDefinitionCreateOperationWithMetaData
  dateToPixel: ReturnType<typeof useTimelineScale>['dateToPixel']
  earliestVersionDate: Date
}

export const SingleParameterDefinitionVersionCircle = ({
  dateToPixel,
  earliestVersionDate,
  version,
}: Props) => {
  const { workspaceKey, branchKey } = useOperationsContext()
  const { server } = useNavigationParams()
  const isVersionActive = useIsParameterVersionActive()
  const isActive = isVersionActive(version)

  const inOutFromDate = getParameterDefinitionInOut(version).fromDate
  const inOutFromDateAsDate = inOutFromDate
    ? dateUtils.dateUtcAsLocal(inOutFromDate).toDate()
    : undefined
  const cxPosition = dateToPixel(inOutFromDateAsDate ?? NO_DATE_SYMBOL)
  const isBeginningOfTime = !inOutFromDate

  return (
    <Tooltip
      tip={
        <ParameterDefinitionInOutTooltipContent
          versions={[version]}
          versionId={version.internalOperation.entity?.name ?? '-'}
          earliestVersionDate={earliestVersionDate}
        />
      }
      isInteractive
    >
      <g>
        <OperationVersionCircle
          cy={ARROW_LINE_WIDTH / 2}
          cx={cxPosition}
          isActive={isActive}
          isDraft={version.isDraft}
          isInherited={false}
          isBeginningOfTime={isBeginningOfTime}
          to={{
            path: configurationItem({
              block: version.block,
              layer: version.layer,
              itemId: version.internalOperation.entity?.name ?? '',
              type: 'parameterDefinition',
              workspaceName: workspaceKey?.name,
              branch: branchKey?.name,
              server: workspaceKey?.branchKey.server ?? server,
              untilChangesetKey: version.isDraft
                ? undefined
                : version.changesetHistory?.changesetKey,
            }),
            atTime: inOutFromDateAsDate
              ? inOutFromDateAsDate
              : earliestVersionDate,
          }}
        />
      </g>
    </Tooltip>
  )
}
